import { AttachmentListEML } from '@/components/attachment-list/attachment-list-eml';
import { type FC, useMemo } from 'react';
import {
  getImageHighlightsForEML,
  PROCESSED_STATUSES,
} from '@/screens/order-inbounds/utils';
import {
  type ImageMapArea,
  type InboundAttachment,
  type InboundEmlAttachment,
  InboundInlineImageAttachment,
} from '@/components/attachment-list/attachment-list-types';
import { OrderInboundsDetailEmlAttachmentList } from './order-inbounds-detail-eml-attachment-list';
import { type OrderMetadata } from '@/screens/order-inbounds/types';
import { Pressable } from 'react-native';
import type { OnAttachmentListItemPressCallback } from '@/screens/order-inbounds/order-inbounds-detail-tabs/types';

export interface Props {
  attachment: InboundEmlAttachment;
  collapsible?: boolean;
  containerWidth: number;
  HeaderComponent?: React.ReactNode;
  isCollapsed?: boolean;
  onAreaSelect: (area: ImageMapArea) => void;
  onAttachmentListItemPress: OnAttachmentListItemPressCallback;
  onCollapsedChange?: (isCollapsed: boolean) => void;
  onEmlError?: (attachment: InboundAttachment) => void;
  onHighlightElementSelect: (searchInputUUID: string) => void;
  onHtmlHasNoContent?: (attachment: InboundAttachment) => void;
  onInlineImageClick?: (inboundContentId: string, index: number) => void;
  order: OrderMetadata;
  selectedAreaID?: string;
  showTranslation?: boolean;
  translationToggle?: React.ReactNode;
}

export const OrderInboundsDetailEmlContent: FC<Props> = ({
  attachment,
  collapsible,
  containerWidth,
  HeaderComponent,
  isCollapsed = false,
  onAreaSelect,
  onAttachmentListItemPress,
  onCollapsedChange,
  onEmlError,
  onHighlightElementSelect,
  onHtmlHasNoContent,
  onInlineImageClick,
  order,
  selectedAreaID,
  translationToggle,
}) => {
  const { inlineAttachments, regularAttachments } = useMemo<{
    inlineAttachments: InboundInlineImageAttachment[];
    regularAttachments: InboundAttachment[];
  }>(() => {
    const inlineAttachments: InboundInlineImageAttachment[] = [];
    const regularAttachments: InboundAttachment[] = [];

    const uniqueInlineAttachmentUrls = new Set<string>();

    for (const currAttachment of order.attachments ?? []) {
      if (currAttachment.type === 'INLINE_IMAGE') {
        inlineAttachments.push(currAttachment);
        uniqueInlineAttachmentUrls.add(currAttachment.url);
      }
    }

    const uniqueRegularAttachmentNames = new Set<string>();

    for (const currAttachment of order.attachments ?? []) {
      if (currAttachment.type === 'EML') {
        continue;
      }

      if (uniqueInlineAttachmentUrls.has(currAttachment.url)) {
        continue;
      }

      if (Boolean(currAttachment.originalFilename)) {
        const related = uniqueRegularAttachmentNames.has(
          currAttachment.originalFilename!,
        );

        if (!related) {
          regularAttachments.push(currAttachment);
        }

        uniqueRegularAttachmentNames.add(currAttachment.originalFilename!);
      } else {
        regularAttachments.push(currAttachment);
      }
    }

    return {
      inlineAttachments,
      regularAttachments,
    };
  }, [order]);

  const imageHighlights = useMemo(
    () => getImageHighlightsForEML(order),
    [order],
  );

  const showHighlights = !PROCESSED_STATUSES.has(order.status);

  return (
    <>
      {HeaderComponent ? (
        collapsible ? (
          <Pressable onPress={() => onCollapsedChange?.(!isCollapsed)}>
            {HeaderComponent}
          </Pressable>
        ) : (
          HeaderComponent
        )
      ) : null}

      {!collapsible || !isCollapsed ? (
        <>
          {translationToggle}

          <AttachmentListEML
            emlAttachment={attachment}
            imageHighlights={imageHighlights}
            inlineAttachments={inlineAttachments}
            onAreaSelect={onAreaSelect}
            onEmlError={() => onEmlError?.(attachment)}
            onHighlightElementSelect={onHighlightElementSelect}
            onHtmlHasNoContent={() => onHtmlHasNoContent?.(attachment)}
            onInlineImageClick={onInlineImageClick}
            selectedSearchInputUUID={selectedAreaID}
            showHighlights={showHighlights}
            width={containerWidth}
          />

          {regularAttachments.length > 0 ? (
            <OrderInboundsDetailEmlAttachmentList
              attachments={regularAttachments}
              onItemPress={onAttachmentListItemPress}
              order={order}
            />
          ) : null}
        </>
      ) : null}
    </>
  );
};
